/**
 * Enhanced Toast Notifications
 *
 * Extends sonner toasts with:
 * - Action buttons (undo, retry, view)
 * - Progress indicators for long operations
 * - Contextual styling based on operation type
 * - Grouping for related notifications
 */

import { toast as sonnerToast, ExternalToast } from "sonner";

type ToastAction = {
  label: string;
  onClick: () => void;
};

interface EnhancedToastOptions extends ExternalToast {
  /** Primary action button */
  action?: ToastAction;
  /** Secondary cancel/undo action */
  cancel?: ToastAction;
  /** Show as persistent until dismissed */
  persistent?: boolean;
}

/**
 * Success toast with optional action
 */
export function success(message: string, options?: EnhancedToastOptions) {
  return sonnerToast.success(message, {
    ...options,
    action: options?.action
      ? {
          label: options.action.label,
          onClick: options.action.onClick,
        }
      : undefined,
    cancel: options?.cancel
      ? {
          label: options.cancel.label,
          onClick: options.cancel.onClick,
        }
      : undefined,
    duration: options?.persistent ? Infinity : options?.duration || 4000,
  });
}

/**
 * Error toast with retry action
 */
export function error(
  message: string,
  options?: EnhancedToastOptions & { onRetry?: () => void }
) {
  return sonnerToast.error(message, {
    ...options,
    action: options?.onRetry
      ? {
          label: "Retry",
          onClick: options.onRetry,
        }
      : options?.action
        ? {
            label: options.action.label,
            onClick: options.action.onClick,
          }
        : undefined,
    duration: options?.persistent ? Infinity : options?.duration || 6000,
  });
}

/**
 * Info toast
 */
export function info(message: string, options?: EnhancedToastOptions) {
  return sonnerToast.info(message, {
    ...options,
    action: options?.action
      ? {
          label: options.action.label,
          onClick: options.action.onClick,
        }
      : undefined,
    duration: options?.duration || 4000,
  });
}

/**
 * Warning toast
 */
export function warning(message: string, options?: EnhancedToastOptions) {
  return sonnerToast.warning(message, {
    ...options,
    action: options?.action
      ? {
          label: options.action.label,
          onClick: options.action.onClick,
        }
      : undefined,
    duration: options?.duration || 5000,
  });
}

/**
 * Promise toast - shows loading, success, and error states
 */
export function promise<T>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((error: Error) => string);
  },
  options?: EnhancedToastOptions
) {
  return sonnerToast.promise(promise, {
    loading: messages.loading,
    success: messages.success,
    error: messages.error,
    ...options,
  });
}

/**
 * Undo toast - shows action with undo option
 * Returns a promise that resolves if action should proceed, rejects if undone
 */
export function withUndo(
  message: string,
  options: {
    undoLabel?: string;
    duration?: number;
    onUndo: () => void;
  }
): Promise<void> {
  return new Promise((resolve, reject) => {
    let undone = false;

    const toastId = sonnerToast(message, {
      action: {
        label: options.undoLabel || "Undo",
        onClick: () => {
          undone = true;
          options.onUndo();
          reject(new Error("Action undone"));
        },
      },
      duration: options.duration || 5000,
      onDismiss: () => {
        if (!undone) {
          resolve();
        }
      },
      onAutoClose: () => {
        if (!undone) {
          resolve();
        }
      },
    });
  });
}

/**
 * Loading toast that can be updated
 */
export function loading(message: string, options?: ExternalToast) {
  return sonnerToast.loading(message, options);
}

/**
 * Dismiss a specific toast or all toasts
 */
export function dismiss(toastId?: string | number) {
  sonnerToast.dismiss(toastId);
}

/**
 * Update an existing toast
 */
export function update(
  toastId: string | number,
  message: string,
  options?: EnhancedToastOptions
) {
  return sonnerToast(message, {
    ...options,
    id: toastId,
  });
}

// Specific use case helpers

/**
 * Created item toast with "View" action
 */
export function created(
  itemName: string,
  options: { viewPath?: string; onView?: () => void }
) {
  return success(`${itemName} created successfully`, {
    action: options.viewPath
      ? {
          label: "View",
          onClick: () => {
            window.location.href = options.viewPath!;
          },
        }
      : options.onView
        ? {
            label: "View",
            onClick: options.onView,
          }
        : undefined,
  });
}

/**
 * Deleted item toast with "Undo" action
 */
export function deleted(
  itemName: string,
  options: {
    onUndo: () => void;
  }
) {
  return success(`${itemName} deleted`, {
    cancel: {
      label: "Undo",
      onClick: options.onUndo,
    },
    duration: 5000,
  });
}

/**
 * Import success toast with details
 */
export function imported(
  count: number,
  type: string,
  options?: { viewPath?: string }
) {
  return success(`${count} ${type}${count !== 1 ? "s" : ""} imported`, {
    action: options?.viewPath
      ? {
          label: "View",
          onClick: () => {
            window.location.href = options.viewPath!;
          },
        }
      : undefined,
  });
}

/**
 * Export success toast with download action
 */
export function exported(fileName: string, options?: { downloadUrl?: string }) {
  return success(`${fileName} exported`, {
    action: options?.downloadUrl
      ? {
          label: "Download",
          onClick: () => {
            window.open(options.downloadUrl, "_blank");
          },
        }
      : undefined,
  });
}

// Default export for drop-in replacement
export const toast = {
  success,
  error,
  info,
  warning,
  promise,
  loading,
  dismiss,
  update,
  withUndo,
  created,
  deleted,
  imported,
  exported,
  // Passthrough for raw sonner access
  raw: sonnerToast,
  // Direct passthrough for custom toasts
  custom: sonnerToast.custom,
};

export default toast;
